import React, { useState } from 'react';
import cn from 'classnames';
import PropTypes from 'prop-types';
import { useTranslation } from 'react-i18next';

import { CardFront, CardBack } from '../../components/Card';

import { ReactComponent as BulbIcon } from '../../icons/bulb.svg';
import { ReactComponent as LogoIcon } from '../../icons/logo.svg';
import { ReactComponent as MessageIcon } from '../../icons/message.svg';
import { ReactComponent as EmailIcon } from '../../icons/mail.svg';

import s from './Phone.module.css';

const cutText = ({ text, length }) => {
  if (!text) {
    return '';
  }

  if (text.length > length) {
    return text.slice(0, length) + '...';
  }

  return text;
};

const Hint = React.memo(({ hint }) => {
  const [t] = useTranslation();
  return (
    <div className={s.hintContainer}>
      <BulbIcon />
      <b className={s.hintTitle}>{t('Совет')}</b>
      <p className={s.hint}>{hint}</p>
    </div>
  );
});

const Phone = ({
  notificationText,
  length,
  notificationTitle,
  notificationType,
  hint,
  type,
  placeholderText,
  cardSide,
  loading,
  iconSrc,
  ...props
}) => {
  const [t] = useTranslation();
  const pushTitle = cutText({ text: `${notificationTitle} | WALLET`, length: 23 });
  const notificationBody = cutText({
    text: notificationText,
    length,
  });

  const renderNotification = () => {
    switch (notificationType) {
      case 'PUSH':
        return (
          <div className={s.notification}>
            <div className={s.header}>
              <div className={s.left}>
                {iconSrc ? (
                  <img className={s.pushIcon} alt="push icon" src={iconSrc} />
                ) : (
                  <div className={s.logo}>
                    <LogoIcon width={16} height={19} />
                  </div>
                )}
                <b className={s.title}>{pushTitle}</b>
              </div>
              <span>{t('сейчас')}</span>
            </div>
            <div className={s.body}>
              <span>{notificationBody || t('Текстовое сообщение')}</span>
            </div>
          </div>
        );
      case 'SMS':
        return (
          <div className={s.notification}>
            <div className={s.header}>
              <div className={s.left}>
                <div className={s.logo}>
                  <MessageIcon />
                </div>
                <b className={s.title}>MESSAGES</b>
              </div>
              <span>{t('сейчас')}</span>
            </div>
            <div className={s.body}>
              <b className={s.bodyTitle}>{notificationTitle}</b>
              <span>{notificationBody || t('Текстовое сообщение')}</span>
            </div>
          </div>
        );
      case 'EMAIL':
        return (
          <div className={s.notification}>
            <div className={s.header}>
              <div className={s.left}>
                <div className={s.logo}>
                  <EmailIcon />
                </div>
                <b className={s.title}>MAIL</b>
              </div>
              <span>{t('сейчас')}</span>
            </div>
            <div className={s.body}>
              <b className={s.bodyTitle}>{notificationTitle}</b>
              <span>{notificationBody || t('Текстовое сообщение')}</span>
            </div>
          </div>
        );
      default:
        return null;
    }
  };

  const renderContent = () => {
    switch (type) {
      case 'notification':
        return (
          <>
            <div className={s.notification}>{renderNotification()}</div>
            <Hint hint={hint} />
          </>
        );
      case 'designer':
        const Card = cardSide === 'front' ? CardFront : CardBack;

        return <Card {...props} />;
      default:
        return null;
    }
  };

  const [phoneLoading, setPhoneLoading] = useState(true);

  return (
    <div className={cn(s.root, (phoneLoading || loading) && s.loading)}>
      <img
        alt="phone"
        className={s.phone}
        src={`${process.env.PUBLIC_URL}/images/iphone.png`}
        onLoad={() => setPhoneLoading(false)}
      />
      <div className={s.content}>{renderContent()}</div>
    </div>
  );
};

Phone.defaultProps = {
  type: 'notification',
  notificationTitle: 'Loyal UP',
  notificationType: 'PUSH',
  length: 55,
  cardSide: 'front',
  loading: false,
};

Phone.propTypes = {
  loading: PropTypes.bool,
  type: PropTypes.oneOf(['notification', 'designer']),
  notificationType: PropTypes.oneOf(['PUSH', 'SMS', 'EMAIL']),
  onSetBackgroundImage: PropTypes.func,
  cardSide: PropTypes.oneOf(['front', 'back']),
  iconSrc: PropTypes.string,
};

export default Phone;
